/* REACT */
import React, { useMemo } from 'react'

/* 3RD PARTIES */
import { connect } from 'react-redux'
import { Rating, RoundedStar } from '@smastrom/react-rating'

/* STORE */
import { setIsPopoverChatOpen } from '../../../../store/general/actions'

/* STYLES */
import {
  ItemImageWrapper,
  ItemImage,
  Title,
  LabelBottomWrapper,
  LabelBottom,
  TitleWrapper,
  GridItemWrapper,
  TripDetailsWrapper,
  TripSecondaryDetailsWrapper,
  TripSecondaryDetailsItemWrapper,
  RelevancyInfo,
  GridItemDetailsWrapper,
  TripSecondaryDetails,
  Location,
  TitleRow,
  BottomRow,
  DurationGroup,
  DurationText,
  PriceText,
  ChevronWrapper,
  PriceSuffix,
  PriceGroup,
  DurationIcon,
} from './style'

/* UTILS/CONSTANTS*/
import { CLOUDFRONT_BASE_URL } from '../../../../utils/constants'
import { Analytics, getTranslations } from '../../../../utils'
import { logAction } from '../../../../utils/actionLogger'

/*ASSETS*/
const NoImage = `${CLOUDFRONT_BASE_URL}/assets/images/NoImage.png`

const TripItem = ({
  index,
  item,
  language,
  setIsPopoverChatOpen,
  openPopoverChatOnClickthrough,
  showRelevancyInfo,
  randomMatch,
  organizationDetails,
}) => {
  const {
    name,
    images,
    price,
    destinations,
    duration,
    label_bottom,
    rating_value,
    rating_total_value,
    description,
  } = item

  const itemImage =
    images?.length ? images[0]?.url || images[0]?.Url || NoImage : NoImage

  const destinationsText = useMemo(() => {
    if (destinations?.length) {
      let text = ''
      let i = 0
      for (let destination of destinations) {
        if (i) text += `, `
        text += `${destination}`
        i += 1
      }
      return text
    }
    return ''
  }, [destinations])

  if (!organizationDetails?.slug) {
    return null
  }

  const getRelevancyColor = () => {
    if (randomMatch) return 'orange'
    return 'green'
  }

  const getPriceParts = priceStr => {
    if (!priceStr) return { main: '', suffix: '' }
    // Normalize whitespace
    const normalized = String(priceStr).trim()
    const hasPp = /p\.p\./i.test(normalized)
    if (hasPp) {
      const mainPart = normalized.replace(/\s*p\.p\.\s*/i, '').trim()
      return { main: mainPart, suffix: 'p.p.' }
    }
    // Fallback: split last space group to separate trailing text if any
    const match = normalized.match(/^(.*?[\d,.]+)(.*)$/)
    if (match) {
      return { main: match[1].trim(), suffix: match[2].trim() }
    }
    return { main: normalized, suffix: '' }
  }

  return (
    <GridItemWrapper
      target={
        organizationDetails?.openNewTabOnClickthroughGrid ? '_blank' : '_self'
      }
      href={item.url}
      onClick={() => {
        Analytics.getInstance().sendEvent('maya_click_grid_item', {
          item_entity: 'trips',
          item_title: name,
          item_subtitle: price,
          item_destination: destinations?.[0],
          item_url: item.url,
        })
        logAction({
          name: 'url_click_grid',
          url: item.url,
          linkLabel: name,
        })
        if (openPopoverChatOnClickthrough) {
          setIsPopoverChatOpen(true)
        }
      }}
    >
      <GridItemDetailsWrapper id={`grid-item-details-wrapper-${index}`}>
        <ItemImageWrapper>
          <ItemImage index={index} backgroundImage={itemImage} />
        </ItemImageWrapper>
        <TripDetailsWrapper>
          <TripSecondaryDetailsWrapper>
            {randomMatch && showRelevancyInfo ? (
              <RelevancyInfo
                style={{
                  backgroundColor: getRelevancyColor(),
                }}
              >
                {getTranslations('relevancy_info.partial_match')}
              </RelevancyInfo>
            ) : null}

            {name || (rating_value && rating_total_value) ? (
              <TitleRow>
                {name ? (
                  <TitleWrapper>
                    <Title dangerouslySetInnerHTML={{ __html: name }} />
                  </TitleWrapper>
                ) : null}
                {rating_value && rating_total_value ? (
                  <Rating
                    style={{ maxWidth: 80 }}
                    value={rating_value}
                    items={rating_total_value}
                    readOnly
                    itemStyles={{
                      itemShapes: RoundedStar,
                      activeFillColor: '#F9AF1C',
                      inactiveFillColor: '#E7E7E7',
                    }}
                  />
                ) : null}
              </TitleRow>
            ) : null}

            {destinations?.length ? <Location>{destinationsText}</Location> : null}

            {label_bottom ? (
              <LabelBottomWrapper>
                <LabelBottom>{label_bottom}</LabelBottom>
              </LabelBottomWrapper>
            ) : null}

            {description ? (
              <TripSecondaryDetails>
                {description?.length > 140
                  ? `${description.substring(0, 140)}...`
                  : description}
              </TripSecondaryDetails>
            ) : null}
          </TripSecondaryDetailsWrapper>
          {duration || price ? (
            <BottomRow>
              <DurationGroup>
                <DurationIcon aria-hidden='true'>
                  <svg width='16' height='16' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <circle cx='10' cy='10' r='9' fill='#999999'/>
                    <path d='M10 5v5l3 2' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                  </svg>
                </DurationIcon>
                <DurationText>{duration}</DurationText>
              </DurationGroup>
              <PriceGroup>
                <PriceText>{getPriceParts(price).main}</PriceText>
                {getPriceParts(price).suffix ? (
                  <PriceSuffix>{getPriceParts(price).suffix}</PriceSuffix>
                ) : null}
              </PriceGroup>
            </BottomRow>
          ) : null}
        </TripDetailsWrapper>
        <ChevronWrapper aria-hidden='true'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='28'
            height='28'
            viewBox='0 0 16 17'
            fill='none'
          >
            <path
              d='M6 3.83337L10.6667 8.50004L6 13.1667'
              stroke='currentColor'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </ChevronWrapper>
      </GridItemDetailsWrapper>
    </GridItemWrapper>
  )
}

const mapStateToProps = state => ({
  openPopoverChatOnClickthrough: state?.general?.openPopoverChatOnClickthrough,
  organizationDetails: state?.maya?.organizationDetails,
})

const mapDispatchToProps = dispatch => ({
  setIsPopoverChatOpen: isOpen => dispatch(setIsPopoverChatOpen(isOpen)),
})

export default connect(mapStateToProps, mapDispatchToProps)(TripItem)


